import { useMemo } from "react";
import { split12h, to24h } from "../utils/time";

/**
 * 12-hour time input. Renders Hour (1-12), Minute (00-59) and AM/PM,
 * but its public value/onChange contract is standard 24h "HH:MM" so nothing
 * else in the app needs to change (DB still stores 24h).
 *
 * Props:
 *   - value: "HH:MM" (24h) string
 *   - onChange: (next24hStr) => void
 *   - testId?: string prefix for data-testid attributes
 *   - ariaLabel?: string
 *   - disabled?: bool
 */
export default function TimeInput12h({ value, onChange, testId = "time", ariaLabel, disabled = false }) {
  const parts = useMemo(() => split12h(value || ""), [value]);

  const emit = (next) => {
    const hour12 = next.hour12 || parts.hour12 || "12";
    const minute = next.minute ?? parts.minute ?? "00";
    const meridiem = next.meridiem || parts.meridiem || "AM";
    onChange(to24h(hour12, minute, meridiem));
  };

  const hourOptions = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0"));

  return (
    <div className={`inline-flex items-stretch rounded-lg border border-stone-300 overflow-hidden bg-white ${disabled ? "opacity-60" : ""}`} aria-label={ariaLabel}>
      <select
        data-testid={`${testId}-hour`}
        value={parts.hour12 ? String(parts.hour12).padStart(2, "0") : "12"}
        onChange={(e) => emit({ hour12: e.target.value })}
        disabled={disabled}
        className="px-2 py-2 text-sm bg-transparent focus:outline-none focus:ring-2 focus:ring-forest-500"
      >
        {hourOptions.map((h) => <option key={h} value={h}>{h}</option>)}
      </select>
      <span className="px-0.5 flex items-center text-stone-400 select-none">:</span>
      <input
        data-testid={`${testId}-minute`}
        type="number"
        min={0}
        max={59}
        value={parts.minute || "00"}
        onChange={(e) => {
          let v = String(e.target.value).replace(/\D/g, "").slice(0, 2);
          if (v === "") v = "0";
          let n = parseInt(v, 10);
          if (isNaN(n)) n = 0;
          n = Math.max(0, Math.min(59, n));
          emit({ minute: String(n).padStart(2, "0") });
        }}
        disabled={disabled}
        className="w-12 px-1 py-2 text-sm bg-transparent text-center focus:outline-none focus:ring-2 focus:ring-forest-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
      />
      <select
        data-testid={`${testId}-meridiem`}
        value={parts.meridiem || "AM"}
        onChange={(e) => emit({ meridiem: e.target.value })}
        disabled={disabled}
        className="px-2 py-2 text-sm bg-stone-50 border-l border-stone-200 font-medium focus:outline-none focus:ring-2 focus:ring-forest-500"
      >
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  );
}
